import classNames from 'classnames'
import { DefineComponentProps, requestIdleCallbackFrame } from 'utils'
import './lazy-loading-component.scss'

interface LazyLoadingComponentProps {
  rootMargin?: string
  minHeight?: string
  noRenderWhenNotInView?: boolean
}

export const LazyLoadingComponent = defineComponent<LazyLoadingComponentProps>({
  props: DefineComponentProps<LazyLoadingComponentProps>(['rootMargin', 'minHeight', 'noRenderWhenNotInView']),
  setup(props, { slots }) {
    const isRender = ref(false)
    const isInView = ref(false)
    const observer = ref<IntersectionObserver>()
    const componentRef = ref<HTMLDivElement>()
    const { noRenderWhenNotInView = false } = props

    const getComponentIsVisible = () => {
      requestIdleCallbackFrame(() => {
        isRender.value = true
      })
    }

    const initObserver = () => {
      observer.value = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              isInView.value = true
            } else {
              if (noRenderWhenNotInView) {
                isInView.value = false
              }
            }
          })
        },
        {
          rootMargin: props.rootMargin || '100px 0px',
        },
      )
    }

    onBeforeMount(() => {
      initObserver()
    })

    const disposeObserver = () => {
      observer.value?.disconnect()
      observer.value = undefined
    }

    const handleObserver = () => {
      if (componentRef.value) {
        observer.value?.observe(componentRef.value)
      }
    }

    onBeforeUnmount(() => {
      disposeObserver()
      isInView.value = false
    })

    onMounted(() => {
      nextTick(() => {
        getComponentIsVisible()
        handleObserver()
      })
    })

    return () => {
      return (
        <div ref={componentRef} class="lazy-loading-component-wrapper">
          <div class={classNames('lazy-loading-component', { 'is-in-view': isInView.value })} style={{ minHeight: props.minHeight }}>
            {isRender.value && isInView.value ? slots.default?.() : slots.placeholder?.()}
          </div>
        </div>
      )
    }
  },
})
